import { getDashboardOptions } from "@payezy/api-client/admin/query";
import { Badge, Card, CardBody, CardHeader, EmptyState, PageHeader, Stat, Table, TD, TH, THead, TR } from "@payezy/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { breakKindLabel, healthTone, useStaff } from "../lib/staff";

export function DashboardPage() {
  const me = useStaff();
  const d = useSuspenseQuery({ ...getDashboardOptions(), refetchInterval: 30_000 }).data;
  const tiles = [
    { label: "Awaiting approval", value: d.pending_actions, to: "/actions", perm: "actions:request" },
    { label: "Open recon breaks", value: d.open_breaks, to: "/recon", perm: "recon:manage" },
    { label: "KYC / KYB in review", value: d.onboarding_in_review, to: "/onboarding", perm: "onboarding:review" },
    { label: "Active view-as sessions", value: d.active_view_as, to: "/view-as", perm: "impersonate:read" },
  ].filter((t) => me.can(t.perm));
  return (
    <>
      <PageHeader title={`Hi ${me.name.split(" ")[0] || "there"}`} description="Work queues and platform health." />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to}>
            <Card className="transition hover:ring-ink-300"><CardBody><Stat label={t.label} value={t.value} /></CardBody></Card>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {me.can("partners:view") && (
          <Card>
            <CardHeader title="Partners" description="Circuit state from recent partner API calls." />
            <Table>
              <THead><TR><TH>Partner</TH><TH>State</TH><TH className="text-right">Calls</TH><TH className="text-right">Error rate</TH></TR></THead>
              <tbody>
                {d.partners.map((p) => (
                  <TR key={p.partner}>
                    <TD className="font-medium">{p.partner}</TD>
                    <TD><Badge tone={healthTone[p.state]}>{p.state}</Badge>{p.override && <span className="ml-2 text-xs text-ink-500">override</span>}</TD>
                    <TD className="text-right tabular-nums">{p.calls}</TD>
                    <TD className="text-right tabular-nums">{(p.error_rate * 100).toFixed(1)}%</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {me.can("recon:manage") && (
          <Card>
            <CardHeader title="Open breaks" description="By kind and partner." />
            {d.breaks_by_kind.length === 0 ? (
              <CardBody><EmptyState icon={<CheckCircle2 className="size-6 text-emerald-600" />} title="Books are reconciled" description="No open breaks." /></CardBody>
            ) : (
              <Table>
                <THead><TR><TH>Kind</TH><TH>Partner</TH><TH className="text-right">Open</TH></TR></THead>
                <tbody>
                  {d.breaks_by_kind.map((b) => (
                    <TR key={b.kind + b.partner}>
                      <TD>{breakKindLabel[b.kind] ?? b.kind}</TD>
                      <TD>{b.partner}</TD>
                      <TD className="text-right tabular-nums">{b.open}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
